import React from 'react';
import Button from '../ui/Button';
import { CheckCircle } from 'react-feather';
import type { OfferStackItem } from '../../types';
import AnimatedSection from '../ui/AnimatedSection';
import { useTranslation } from 'react-i18next';

interface OfferStackProps {
    offerStack: OfferStackItem[];
}

const OfferStack: React.FC<OfferStackProps> = ({ offerStack }) => {
    const { t } = useTranslation('common');
    // DEFINITIVE FIX: Add a guard to prevent the component from crashing if the offerStack prop is not a valid array.
    if (!offerStack || !Array.isArray(offerStack)) {
        return null;
    }

    const totalValue = offerStack.reduce((sum, item) => {
        // DEFINITIVE FIX: Add robust guards to prevent crashes if item or item.value is missing or not a string.
        if (item && typeof item.value === 'string' && item.value.startsWith('$')) {
            const numericString = item.value.substring(1).replace(/,/g, '');
            const value = parseInt(numericString, 10);
            return sum + (isNaN(value) ? 0 : value);
        }
        return sum;
    }, 0);

    return (
        <AnimatedSection>
            <div className="py-24">
                <div className="mx-auto max-w-4xl px-6">
                    <div className="mx-auto mb-12 text-center">
                        <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('resultsPage.offerStackTitle')}</h2>
                        <p className="mt-4 text-lg text-white/80">{t('resultsPage.offerStackSubtitle')}</p>
                    </div>

                    <div className="rounded-3xl border border-white/10 bg-white/5 shadow-card glass-effect overflow-hidden">
                        <div className="p-8">
                            {offerStack.map((item, index) => (
                                <div key={index} className={`flex justify-between items-start gap-6 ${index < offerStack.length - 1 ? 'pb-6 mb-6 border-b border-white/10' : ''}`}>
                                    <div className="flex-shrink-0 text-brand-gold-light mt-1">
                                        <CheckCircle size={24} />
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className="font-bold text-lg">{item.title}</h4>
                                        <p className="text-sm text-white/80">{item.description}</p>
                                    </div>
                                    <div className="flex-shrink-0 text-right">
                                        <p className="font-bold text-lg">{item.value?.startsWith('$') ? item.value : ''}</p>
                                        <p className="text-sm text-white/80">{!item.value?.startsWith('$') ? item.value : 'Value'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="p-8 bg-black/30 text-center">
                            <p className="text-xl">{t('resultsPage.offerStackTotalValue')} <span className="line-through text-white/70">${totalValue.toLocaleString()}</span></p>
                            <p className="text-5xl font-bold gold-gradient-text mt-2">{t('resultsPage.offerStackPrice')}</p>
                        </div>
                    </div>

                    <div className="mt-12 text-center">
                        <Button to="/contact" variant="primary" className="px-10 py-5 text-xl">
                            {t('resultsPage.offerStackButton')}
                        </Button>
                        <p className="mt-3 text-sm text-white/70">{t('resultsPage.offerStackDisclaimer')}</p>
                    </div>
                </div>
            </div>
        </AnimatedSection>
    );
};

export default OfferStack;